// src/components/cofounder/CoFounderPanel.js
import React, { useState } from 'react';
import { X, Maximize2, Minimize2, MessageSquare, Lightbulb, Zap, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import CoFounderMessageList from './CoFounderMessageList';
import CoFounderInput from './CoFounderInput';
import InsightsList from './InsightsList';
import ActionsList from './ActionsList';
import QuickActions from './QuickActions';
import { useCoFounder } from '../../context/CoFounderContext';

const CoFounderPanel = () => {
  const {
    isOpen, setIsOpen, isExpanded, setIsExpanded,
    sendMessage, isLoading, messages, unreadInsightCount, unreadActionCount
  } = useCoFounder();

  const [activeTab, setActiveTab] = useState('chat');

  if (!isOpen) return null;

  const handleInsightAsk = (insight) => {
    setActiveTab('chat');
    sendMessage(`Tell me more about this: ${insight.title}. ${insight.description}`);
  };

  return (
    <div className={`fixed top-4 bottom-20 right-4 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200
      flex flex-col transition-all duration-300
      ${isExpanded ? 'w-[500px]' : 'w-[380px]'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-[#190192] to-[#4f46e5] rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-white bg-opacity-20 flex items-center justify-center">
            <span className="text-white text-sm font-bold">co</span>
          </div>
          <span className="text-white font-semibold text-sm">coFounder</span>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/cofounder" onClick={() => setIsOpen(false)}
                className="p-1.5 text-white text-opacity-70 hover:text-opacity-100 transition-colors">
            <ExternalLink size={14} />
          </Link>
          <button onClick={() => setIsExpanded(!isExpanded)}
                  className="p-1.5 text-white text-opacity-70 hover:text-opacity-100 transition-colors">
            {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={() => setIsOpen(false)}
                  className="p-1.5 text-white text-opacity-70 hover:text-opacity-100 transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors
            ${activeTab === 'chat' ? 'text-[#190192] border-b-2 border-[#190192]' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <MessageSquare size={13} />
          Chat
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors relative
            ${activeTab === 'actions' ? 'text-[#190192] border-b-2 border-[#190192]' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Zap size={13} />
          Actions
          {unreadActionCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
              {unreadActionCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors relative
            ${activeTab === 'insights' ? 'text-[#190192] border-b-2 border-[#190192]' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Lightbulb size={13} />
          Insights
          {unreadInsightCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
              {unreadInsightCount}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'chat' ? (
        <>
          <CoFounderMessageList />
          {messages.length === 0 && <QuickActions onSelect={sendMessage} />}
          <CoFounderInput onSend={sendMessage} isLoading={isLoading} />
        </>
      ) : activeTab === 'actions' ? (
        <div className="flex-1 overflow-y-auto p-3">
          <ActionsList compact />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3">
          <InsightsList onAskAbout={handleInsightAsk} />
        </div>
      )}
    </div>
  );
};

export default CoFounderPanel;
